import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Globe, Sparkles, CheckCircle2, XCircle, ExternalLink, RefreshCw, Zap, MapPin, Clock, DollarSign, Instagram, Link2, Users, Landmark } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { LocationPickerModal } from '../LocationPickerModal';
import { VenueAutocomplete } from '../VenueAutocomplete';
import { OrganizerAutocomplete } from '../OrganizerAutocomplete';
import InstagramSourcesPanel from './InstagramSourcesPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = { pending: 'Pendiente', approved: 'Aprobado', rejected: 'Rechazado' };
const STATUS_COLOR = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

// Color per pipeline source (visual identity so admin can spot which scraper each card belongs to)
const SOURCE_ACCENT = {
  vamopo:       'text-fuchsia-700 bg-fuchsia-50 border-fuchsia-100',
  passline:     'text-blue-700    bg-blue-50    border-blue-100',
  ticketplus:   'text-emerald-700 bg-emerald-50 border-emerald-100',
  puntoticket:  'text-orange-700  bg-orange-50  border-orange-100',
  portaldisc:   'text-red-700     bg-red-50     border-red-100',
  ticketmaster: 'text-indigo-700  bg-indigo-50  border-indigo-100',
  welcu:        'text-pink-700    bg-pink-50    border-pink-100',
};

function ScraperConfigCard({ cfg, regions, running, onRun, onSave, onSelectFilter }) {
  const [local, setLocal] = React.useState(cfg);
  React.useEffect(() => { setLocal(cfg); }, [cfg]);

  const settings = local.settings || {};
  const selectedRegion = (settings.regions && settings.regions[0]) || '';

  const patchSettings = (patch) => {
    const merged = { ...settings, ...patch };
    setLocal({ ...local, settings: merged });
    onSave({ settings: merged });
  };

  const accent = SOURCE_ACCENT[cfg.key] || 'text-gray-700 bg-gray-50 border-gray-100';
  const active = cfg.is_active !== false;

  return (
    <div className={`rounded-xl bg-white border p-3 flex flex-col gap-2 ${active ? '' : 'opacity-60'}`} data-testid={`scraper-card-${cfg.key}`}>
      <div className="flex items-center justify-between">
        <button
          onClick={onSelectFilter}
          className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${accent} hover:opacity-80`}
          title="Filtrar Pendientes por esta fuente"
        >
          {cfg.key}
        </button>
        <label className="flex items-center gap-1 cursor-pointer" title="Activar/desactivar auto-scraper">
          <span className="text-[10px] text-gray-500 uppercase">Auto</span>
          <input type="checkbox"
            checked={active}
            onChange={(e) => { setLocal({ ...local, is_active: e.target.checked }); onSave({ is_active: e.target.checked }); }}
            className="w-3.5 h-3.5 accent-emerald-600" data-testid={`cfg-${cfg.key}-active`} />
        </label>
      </div>
      {cfg.label && <p className="text-[11px] text-gray-500 leading-tight">{cfg.label}</p>}
      <div className="flex items-center gap-1.5 flex-wrap">
        <div className="flex items-center gap-1 bg-gray-50 rounded-md border px-1.5 py-0.5" title="Cada cuántos días">
          <span className="text-[10px] text-gray-400 uppercase">Frec.</span>
          <input type="number" min="1" max="90"
            value={local.frequency_days ?? 1}
            onChange={(e) => setLocal({ ...local, frequency_days: Math.max(1, parseInt(e.target.value) || 1) })}
            onBlur={() => onSave({ frequency_days: local.frequency_days })}
            className="w-9 outline-none text-xs font-semibold text-right bg-transparent" data-testid={`cfg-${cfg.key}-freq`} />
          <span className="text-[10px] text-gray-400">d</span>
        </div>
        <div className="flex items-center gap-1 bg-gray-50 rounded-md border px-1.5 py-0.5" title="Máximo por corrida. 0 = sin límite (todos los eventos disponibles).">
          <span className="text-[10px] text-gray-400 uppercase">Máx</span>
          <input type="number" min="0" max="2000"
            value={local.max_events ?? 0}
            onChange={(e) => setLocal({ ...local, max_events: Math.max(0, Math.min(2000, parseInt(e.target.value) || 0)) })}
            onBlur={() => onSave({ max_events: local.max_events })}
            className="w-14 outline-none text-xs font-semibold text-right bg-transparent" data-testid={`cfg-${cfg.key}-max`} />
          {local.max_events === 0 && <span className="text-[9px] text-emerald-700 font-bold" title="0 = todos los eventos disponibles (sin tope)">TODO</span>}
        </div>
        <label className="flex items-center gap-1 bg-gray-50 rounded-md border px-1.5 py-0.5 cursor-pointer" title="Auto-enriquecer con Claude">
          <span className="text-[10px] text-gray-400 uppercase">IA</span>
          <input type="checkbox"
            checked={!!local.auto_enrich}
            onChange={(e) => { setLocal({ ...local, auto_enrich: e.target.checked }); onSave({ auto_enrich: e.target.checked }); }}
            className="w-3.5 h-3.5 accent-emerald-600" data-testid={`cfg-${cfg.key}-ai`} />
        </label>
      </div>
      <div className="flex items-center gap-1.5">
        <select
          value={selectedRegion}
          onChange={(e) => patchSettings({ regions: e.target.value ? [e.target.value] : [] })}
          className="text-xs bg-gray-50 border rounded-md px-1.5 py-1 outline-none flex-1"
          data-testid={`cfg-${cfg.key}-region`}
        >
          <option value="">🌎 Todas las regiones</option>
          {regions.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
        </select>
        <Button size="sm" onClick={onRun} disabled={running || !active}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-2"
          data-testid={`cfg-${cfg.key}-run`}>
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </div>
      {(cfg.last_run_at || cfg.next_run_at) && (
        <p className="text-[10px] text-gray-400 leading-tight" data-testid={`cfg-${cfg.key}-schedule`}>
          {cfg.last_run_at && <>Última: {new Date(cfg.last_run_at).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}</>}
          {cfg.next_run_at && <><br />Próxima: {new Date(cfg.next_run_at).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}</>}
        </p>
      )}
    </div>
  );
}

export default function ScrapingTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningKey, setRunningKey] = useState(null);
  const [runningAll, setRunningAll] = useState(false);
  const [enrichingId, setEnrichingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [communeFilter, setCommuneFilter] = useState('all');
  // Client-side "fecha de corte" filter: only show pending events whose
  // start_date is on/after this date. Empty = no filter. Applies only to the
  // Pendientes tab. Events without a start_date pass through (recurring/permanent).
  const [cutoffDate, setCutoffDate] = useState('');
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [scraperConfigs, setScraperConfigs] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkRejecting, setBulkRejecting] = useState(false);
  // Selection for bulk-reject. Reset on filter changes.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [backfilling, setBackfilling] = useState(false);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [pendingRegionId, setPendingRegionId] = useState('');

  // Region derived from the selected commune (for the cascading selector)
  const selectedRegionId = React.useMemo(() => {
    if (editing?.commune_id) {
      const com = communes.find((c) => c.id === editing.commune_id);
      if (com?.region_id) return com.region_id;
    }
    return pendingRegionId || '';
  }, [editing?.commune_id, communes, pendingRegionId]);

  // Auto-match venue_name against the venues master when opening the edit modal.
  // If the AI enrichment left a venue name that exists in our Lugares mantenedor,
  // pull its address/coords/commune so the admin doesn't need to search again.
  const autoMatchedRef = React.useRef(null);
  useEffect(() => {
    if (!editing?.id || !editing?.venue_name) { autoMatchedRef.current = null; return; }
    // Only try once per opened event, and only if we don't already have coords.
    if (autoMatchedRef.current === editing.id) return;
    if (editing.latitude && editing.longitude) { autoMatchedRef.current = editing.id; return; }
    autoMatchedRef.current = editing.id;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/venues/search?q=${encodeURIComponent(editing.venue_name)}&limit=5`);
        const list = res.data || [];
        // Exact (case-insensitive) match only — avoid overwriting on partial hits.
        const target = editing.venue_name.trim().toLowerCase();
        const match = list.find((v) => (v.name || '').trim().toLowerCase() === target);
        if (!match) return;
        setEditing((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            venue_name: match.name,
            address: match.address || prev.address,
            latitude: match.latitude ?? prev.latitude,
            longitude: match.longitude ?? prev.longitude,
            commune_id: match.commune_id || prev.commune_id,
          };
        });
        if (match.region_id) setPendingRegionId(match.region_id);
        toast.success(`Recinto autocompletado: ${match.name}`);
      } catch { /* silently ignore — user can search manually */ }
    })();
  }, [editing?.id, editing?.venue_name]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, limit: '500' });
      if (sourceFilter !== 'all') params.set('source', sourceFilter);
      if (communeFilter !== 'all') params.set('commune_id', communeFilter);
      else if (regionFilter !== 'all') params.set('region_id', regionFilter);
      const scrapedUrl = `${API_URL}/api/admin/scraping/scraped?${params.toString()}`;
      const [scrapedRes, catsRes, comsRes, regsRes, cfgRes] = await Promise.all([
        axios.get(scrapedUrl),
        axios.get(`${API_URL}/api/admin/categories`),
        axios.get(`${API_URL}/api/admin/communes`),
        axios.get(`${API_URL}/api/admin/regions`),
        axios.get(`${API_URL}/api/admin/scraping/configs`).catch(() => ({ data: [] })),
      ]);
      setItems(scrapedRes.data || []);
      setCategories(catsRes.data || []);
      setCommunes(comsRes.data || []);
      setRegions(regsRes.data || []);
      setScraperConfigs(cfgRes.data || []);
    } catch (e) {
      toast.error('No se pudieron cargar los eventos scrapeados');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, [statusFilter, sourceFilter, regionFilter, communeFilter]);
  // Reset selection when filters change or list reloads.
  useEffect(() => { setSelectedIds(new Set()); }, [statusFilter, sourceFilter, regionFilter, communeFilter, cutoffDate]);

  // Apply the client-side "fecha de corte" filter on top of the server-side
  // list. Only affects the Pendientes tab. Recurring/permanent events (no
  // fixed start_date, or explicitly flagged) always pass through so they
  // don't disappear when the admin picks a future date.
  const visibleItems = React.useMemo(() => {
    if (statusFilter !== 'pending' || !cutoffDate) return items;
    const cutoff = new Date(`${cutoffDate}T00:00:00`);
    if (Number.isNaN(cutoff.getTime())) return items;
    return items.filter((it) => {
      if (it.is_permanent || it.is_recurring) return true;
      if (!it.start_date) return true;
      const start = new Date(it.start_date);
      if (Number.isNaN(start.getTime())) return true;
      return start >= cutoff;
    });
  }, [items, statusFilter, cutoffDate]);

  const toggleSelect = React.useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAllVisible = () => {
    setSelectedIds(new Set(visibleItems.map((i) => i.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkReject = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`¿Rechazar ${ids.length} evento${ids.length !== 1 ? 's' : ''} seleccionado${ids.length !== 1 ? 's' : ''}? Esta acción marca cada uno como "Rechazado" y los saca de Pendientes.`)) return;
    setBulkRejecting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/scraping/scraped/bulk-reject`, { ids });
      toast.success(`${res.data.rejected} rechazados de ${res.data.requested}`);
      setItems((prev) => prev.filter((it) => !selectedIds.has(it.id)));
      clearSelection();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al rechazar en masa');
    } finally { setBulkRejecting(false); }
  };

  // Reject every visible pending event whose start date is already in the
  // past (skipping permanent/recurring events, which never "expire").
  // Rationale: en la moderación de scraping, un evento que ya arrancó pierde
  // valor como "próximo panorama" aunque su end_date siga vigente (típico en
  // obras de teatro multi-día). El label y este filtro usan el mismo criterio.
  const rejectExpired = async () => {
    const now = new Date();
    const expiredIds = visibleItems
      .filter((it) => {
        if (!it.start_date) return false;
        if (it.is_permanent || it.is_recurring) return false;
        const start = new Date(it.start_date);
        return start < now;
      })
      .map((it) => it.id);
    if (expiredIds.length === 0) {
      toast.info('No hay eventos vencidos en la lista actual');
      return;
    }
    if (!window.confirm(`Se rechazarán ${expiredIds.length} evento${expiredIds.length !== 1 ? 's' : ''} vencido${expiredIds.length !== 1 ? 's' : ''} de los ${visibleItems.length} pendientes visibles. ¿Continuar?`)) return;
    setBulkRejecting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/scraping/scraped/bulk-reject`, { ids: expiredIds });
      toast.success(`${res.data.rejected} vencidos rechazados`);
      const rejectedSet = new Set(expiredIds);
      setItems((prev) => prev.filter((it) => !rejectedSet.has(it.id)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al rechazar vencidos');
    } finally { setBulkRejecting(false); }
  };

  const runScrape = async (configKey) => {
    setRunningKey(configKey);
    try {
      const start = await axios.post(`${API_URL}/api/admin/scraping/vamopo/run?config_key=${configKey}`);
      const jobId = start.data?.job_id;
      if (!jobId) { toast.error('No se pudo iniciar el job'); setRunningKey(null); return; }
      toast.message(`Scraping ${configKey}…`, { description: 'Este proceso puede tardar varios minutos si auto-enrich está activo.' });
      const started = Date.now();
      while (Date.now() - started < 15 * 60_000) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const jr = await axios.get(`${API_URL}/api/admin/scraping/scraping/jobs/${jobId}`);
          const j = jr.data;
          if (j.status === 'completed') {
            const d = j.result || {};
            toast.success(`${configKey}: ${d.inserted} nuevos · ${d.enriched || 0} IA · ${d.skipped_past || 0} pasados · ${d.skipped_duplicate} dup (${d.skipped_rejected || 0} rechazados omitidos)`);
            setStatusFilter('pending');
            await fetchAll();
            break;
          }
          if (j.status === 'failed') { toast.error(`Job ${configKey} falló: ${j.error || 'error'}`); break; }
        } catch { /* keep polling */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al ejecutar scraper');
    } finally { setRunningKey(null); }
  };

  const runAllScrapers = async () => {
    if (!scraperConfigs.length) return;
    if (!window.confirm(`Se lanzarán ${scraperConfigs.length} scrapers en paralelo, sin límite de eventos por fuente. Los resultados aparecerán en Pendientes a medida que terminen. ¿Continuar?`)) return;
    setRunningAll(true);
    try {
      const results = await Promise.allSettled(
        scraperConfigs.map((cfg) =>
          axios.post(`${API_URL}/api/admin/scraping/vamopo/run?config_key=${cfg.key}`)
        )
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      if (failed === 0) {
        toast.success(`✅ ${ok} scrapers iniciados en background. Refresca Pendientes en unos minutos.`);
      } else {
        toast.warning(`${ok} iniciados · ${failed} fallaron al lanzar. Revisa la consola.`);
      }
    } catch (e) {
      toast.error('Error al lanzar los scrapers');
    } finally {
      setRunningAll(false);
    }
  };

  const saveConfig = async (key, patch) => {
    setSavingConfig(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/scraping/configs/${key}`, patch);
      setScraperConfigs((prev) => prev.map((c) => (c.key === key ? res.data : c)));
    } catch (e) { toast.error('Error al guardar config'); }
    finally { setSavingConfig(false); }
  };

  // These are wrapped in useCallback so the memoized card list doesn't
  // re-render on every parent keystroke (typing in the edit modal).
  const enrichOne = React.useCallback(async (id) => {
    setEnrichingId(id);
    try {
      const res = await axios.post(`${API_URL}/api/admin/scraping/scraped/${id}/enrich`);
      setItems((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      toast.success('IA aplicada correctamente');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enriquecer con IA');
    } finally { setEnrichingId(null); }
  }, []);

  // State for the duplicate confirmation modal (shown when backend returns 409).
  const [duplicateModal, setDuplicateModal] = React.useState(null);
  // Shape: { scrapedId, scrapedName, existing: {...}, source: 'approve' | 'edit-save-publish' }

  const doApprove = React.useCallback(async (id, allowDuplicate = false) => {
    setItems((prev) => prev.filter((r) => r.id !== id));
    try {
      const qs = allowDuplicate ? '?allow_duplicate=true' : '';
      const res = await axios.post(`${API_URL}/api/admin/scraping/scraped/${id}/approve${qs}`);
      const publishedId = res?.data?.imported_event_id || res?.data?.event_id;
      const publishedName = res?.data?.name;
      const nameSnippet = publishedName ? ` — "${publishedName.slice(0, 60)}${publishedName.length > 60 ? '…' : ''}"` : '';
      toast.success(
        publishedId ? 'Evento publicado ✓' : 'Evento aprobado ✓',
        {
          description: `${nameSnippet}`,
          action: publishedId ? {
            label: 'Abrir',
            onClick: () => window.open(`/event/${publishedId}`, '_blank'),
          } : undefined,
        }
      );
      return true;
    } catch (e) {
      // 409 duplicate → show modal so the operator can confirm bypass.
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'duplicate_event') {
        // Restore the card so the operator can retry from the list.
        const scraped = items.find((r) => r.id === id);
        setItems((prev) => (prev.some((r) => r.id === id) ? prev : (scraped ? [scraped, ...prev] : prev)));
        setDuplicateModal({
          scrapedId: id,
          scrapedName: scraped?.name || '',
          existing: detail.existing,
          source: 'approve',
        });
        return false;
      }
      toast.error(typeof detail === 'string' ? detail : 'Error al aprobar — refresca para restaurar');
      return false;
    }
  }, [items]);

  const approve = React.useCallback((id) => doApprove(id, false), [doApprove]);

  const reject = React.useCallback(async (id) => {
    // Optimistic: hide the card at once so the click feels snappy even under
    // long round-trips. Any failure will surface via toast.
    setItems((prev) => prev.filter((r) => r.id !== id));
    toast.info('Descartado');
    try {
      await axios.post(`${API_URL}/api/admin/scraping/scraped/${id}/reject`);
    } catch (e) {
      toast.error('Error al rechazar — refresca para restaurar');
    }
  }, []);

  const openEditModal = React.useCallback(async (it) => {
    setPendingRegionId('');
    // Normalise end_date to start + 3h whenever the scraped end is missing,
    // equal to start, before start, or an obvious placeholder (00:00 midnight
    // of the same day, or 23:59 of the same day). This is what the admin
    // expects when he clicks "Editar" — a sane default he can tweak.
    const patch = { ...it };
    if (patch.start_date) {
      const startMs = new Date(patch.start_date).getTime();
      const endMs = patch.end_date ? new Date(patch.end_date).getTime() : NaN;
      const startDay = patch.start_date.slice(0, 10);
      const endDay = patch.end_date ? patch.end_date.slice(0, 10) : '';
      const endTime = patch.end_date ? patch.end_date.slice(11, 16) : '';
      const looksPlaceholder =
        !patch.end_date ||
        Number.isNaN(endMs) ||
        endMs <= startMs ||
        (endDay === startDay && (endTime === '00:00' || endTime === '23:59'));
      if (looksPlaceholder) {
        const s = new Date(patch.start_date);
        const e = new Date(s.getTime() + 3 * 60 * 60 * 1000);
        const pad = (n) => String(n).padStart(2, '0');
        patch.end_date = `${e.getFullYear()}-${pad(e.getMonth() + 1)}-${pad(e.getDate())}T${pad(e.getHours())}:${pad(e.getMinutes())}:00`;
      }
    }
    // Show the modal immediately so the admin doesn't wait on network.
    setEditing(patch);
    // Async: if organizer IG is missing, resolve it from the Organizadores
    // catalog by looking up the source URL's domain (or the org name in URL).
    if (!patch.organizer_instagram && patch.source_url) {
      try {
        const res = await axios.get(`${API_URL}/api/organizers/resolve-by-url`, {
          params: { url: patch.source_url },
        });
        if (res.data && res.data.instagram) {
          setEditing((prev) => (prev && prev.id === patch.id && !prev.organizer_instagram
            ? { ...prev, organizer_instagram: res.data.instagram }
            : prev
          ));
        }
      } catch (_) {
        // silent — leaving the field blank is fine
      }
    }
  }, []);

  const bulkApprove = async () => {
    if (!window.confirm(`¿Aprobar y publicar los ${items.length} eventos visibles? Solo los que tengan categoría, comuna e imagen serán publicados.`)) return;
    setBulkApproving(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/scraping/bulk-approve`);
      toast.success(`${res.data.approved} publicados · ${res.data.skipped_missing_fields} saltados por datos incompletos`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en aprobación masiva');
    } finally { setBulkApproving(false); }
  };

  const backfillMissing = async () => {
    const missing = items.filter((i) => i.name && i.main_image && (!i.category_id || !i.commune_id)).length;
    if (missing === 0) { toast.info('No hay eventos con categoría/comuna faltante'); return; }
    if (!window.confirm(`La IA (Claude) va a completar categoría y/o comuna en ~${missing} eventos. Corre en background (~1-2 min). ¿Continuar?`)) return;
    setBackfilling(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/scraping/scraped/backfill-missing`);
      toast.success(res.data.message || `Backfill iniciado para ${res.data.queued} eventos`);
      // Refresh after ~90s to pick up filled fields
      setTimeout(() => { fetchAll(); setBackfilling(false); }, 90000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar auto-completado');
      setBackfilling(false);
    }
  };

  const saveEdit = async () => {
    const { id, ...rest } = editing;
    try {
      const res = await axios.put(`${API_URL}/api/admin/scraping/scraped/${id}`, rest);
      setItems((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      toast.success('Cambios guardados');
      setEditing(null);
      setPendingRegionId('');
    } catch (e) { toast.error('Error al guardar'); }
  };

  const [saveAndPublishing, setSaveAndPublishing] = useState(false);
  const saveAndPublish = async (allowDuplicate = false) => {
    // Save the edits AND publish in a single click — bypasses the "Aprobar"
    // step from the pending list. Useful when the admin already finished
    // reviewing inside the modal and doesn't want to click twice.
    if (saveAndPublishing) return;
    setSaveAndPublishing(true);
    const { id, ...rest } = editing;
    const scrapedName = editing.name;
    try {
      // Step 1: save current field edits.
      await axios.put(`${API_URL}/api/admin/scraping/scraped/${id}`, rest);
      // Step 2: publish. Optimistically remove the row on success.
      const qs = allowDuplicate ? '?allow_duplicate=true' : '';
      const res = await axios.post(`${API_URL}/api/admin/scraping/scraped/${id}/approve${qs}`);
      const publishedId = res?.data?.imported_event_id || res?.data?.event_id;
      const publishedName = res?.data?.name;
      setItems((prev) => prev.filter((r) => r.id !== id));
      setEditing(null);
      setPendingRegionId('');
      toast.success(
        publishedId ? 'Guardado y publicado ✓' : 'Guardado y aprobado ✓',
        {
          description: publishedName ? `"${publishedName.slice(0, 60)}${publishedName.length > 60 ? '…' : ''}"` : undefined,
          action: publishedId ? {
            label: 'Abrir',
            onClick: () => window.open(`/event/${publishedId}`, '_blank'),
          } : undefined,
        }
      );
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'duplicate_event') {
        setDuplicateModal({
          scrapedId: id,
          scrapedName,
          existing: detail.existing,
          source: 'edit-save-publish',
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Error al guardar y publicar');
      }
    } finally {
      setSaveAndPublishing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Sources panel — one card per scraper config */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#064E3B]">Web Scraping</h2>
              <p className="text-sm text-gray-600 max-w-md">Ingesta automatizada por fuente. Cada tarjeta corre en su propia agenda. Filtra por región para acotar el scraping.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={runAllScrapers}
            disabled={runningAll || scraperConfigs.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="scraping-run-all"
          >
            {runningAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
            {runningAll ? 'Lanzando todos…' : 'Refrescar todo (background)'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="scraper-configs-grid">
          {scraperConfigs.map((cfg) => (
            <ScraperConfigCard
              key={cfg.key}
              cfg={cfg}
              regions={regions}
              running={runningKey === cfg.key}
              onRun={() => runScrape(cfg.key)}
              onSave={(patch) => saveConfig(cfg.key, patch)}
              onSelectFilter={() => setSourceFilter(cfg.key)}
            />
          ))}
          {scraperConfigs.length === 0 && (
            <div className="text-sm text-gray-500 italic col-span-full">No hay fuentes configuradas.</div>
          )}
        </div>
      </div>

      {/* Instagram sources panel */}
      <InstagramSourcesPanel onRunComplete={fetchAll} />

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {['pending', 'approved', 'rejected'].map((s) => (
          <button key={s} data-testid={`filter-${s}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${statusFilter === s ? 'bg-[#064E3B] text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>
            {STATUS_LABEL[s]} {statusFilter === s && `· ${s === 'pending' ? visibleItems.length : items.length}`}
          </button>
        ))}
        <div className="flex items-center gap-1 ml-3" data-testid="source-filter">
          <span className="text-[11px] text-gray-500 uppercase tracking-wider">Fuente:</span>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="text-xs bg-white border rounded-full px-2 py-1 outline-none">
            <option value="all">Todas</option>
            {scraperConfigs.map((c) => (
              <option key={c.key} value={c.key}>{c.label || c.key}</option>
            ))}
            <option value="instagram">Instagram</option>
          </select>
        </div>
        <div className="flex items-center gap-1" data-testid="region-filter">
          <span className="text-[11px] text-gray-500 uppercase tracking-wider">Región:</span>
          <select
            value={regionFilter}
            onChange={(e) => { setRegionFilter(e.target.value); setCommuneFilter('all'); }}
            className="text-xs bg-white border rounded-full px-2 py-1 outline-none max-w-[180px]">
            <option value="all">Todas</option>
            {regions.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1" data-testid="commune-filter">
          <span className="text-[11px] text-gray-500 uppercase tracking-wider">Comuna:</span>
          <select
            value={communeFilter}
            onChange={(e) => setCommuneFilter(e.target.value)}
            className="text-xs bg-white border rounded-full px-2 py-1 outline-none max-w-[180px]">
            <option value="all">Todas</option>
            {communes
              .filter((c) => regionFilter === 'all' || c.region_id === regionFilter)
              .map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
          </select>
        </div>
        {statusFilter === 'pending' && (
          <div className="flex items-center gap-1" data-testid="cutoff-date-filter">
            <span className="text-[11px] text-gray-500 uppercase tracking-wider">Fecha de corte:</span>
            <input
              type="date"
              value={cutoffDate}
              onChange={(e) => setCutoffDate(e.target.value)}
              className="text-xs bg-white border rounded-full px-2 py-1 outline-none"
              title="Muestra solo eventos con fecha de inicio desde este día en adelante"
              data-testid="cutoff-date-input"
            />
            {cutoffDate && (
              <button
                type="button"
                onClick={() => setCutoffDate('')}
                className="text-[11px] text-gray-500 hover:text-gray-700 underline ml-1"
                title="Quitar filtro de fecha"
                data-testid="cutoff-date-clear"
              >
                limpiar
              </button>
            )}
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="refresh-scraped-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
          </Button>
          {statusFilter === 'pending' && visibleItems.length > 0 && (() => {
            const missing = visibleItems.filter((i) => i.name && i.main_image && (!i.category_id || !i.commune_id)).length;
            return missing > 0 ? (
              <Button
                size="sm"
                variant="outline"
                onClick={backfillMissing}
                disabled={backfilling}
                className="border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                data-testid="backfill-missing-btn"
                title="La IA rellena categoría y comuna en los eventos que las tengan vacías"
              >
                {backfilling ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                Auto-completar {missing}
              </Button>
            ) : null;
          })()}
          {statusFilter === 'pending' && visibleItems.length > 0 && (() => {
            const approvable = visibleItems.filter((i) => i.name && i.category_id && i.commune_id && i.main_image).length;
            const selCount = selectedIds.size;
            const now = new Date();
            const expiredCount = visibleItems.filter((it) => {
              if (!it.start_date || it.is_permanent || it.is_recurring) return false;
              const start = new Date(it.start_date);
              return start < now;
            }).length;
            return (
              <>
                {expiredCount > 0 && selCount === 0 && (
                  <Button
                    size="sm"
                    onClick={rejectExpired}
                    disabled={bulkRejecting}
                    className="bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                    data-testid="reject-expired-btn"
                    title="Rechaza todos los pendientes cuya fecha de inicio ya pasó"
                  >
                    {bulkRejecting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                    Rechazar {expiredCount} vencido{expiredCount === 1 ? '' : 's'}
                  </Button>
                )}
                {selCount > 0 ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={clearSelection}
                      className="border-gray-300 text-gray-600 hover:bg-gray-50"
                      data-testid="clear-selection-btn"
                    >
                      Quitar selección
                    </Button>
                    <Button
                      size="sm"
                      onClick={bulkReject}
                      disabled={bulkRejecting}
                      className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                      data-testid="bulk-reject-btn"
                    >
                      {bulkRejecting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                      Rechazar {selCount} seleccionado{selCount === 1 ? '' : 's'}
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={selectAllVisible}
                    className="border-gray-300 text-gray-600 hover:bg-gray-50"
                    data-testid="select-all-btn"
                  >
                    Seleccionar todos ({visibleItems.length})
                  </Button>
                )}
                <Button size="sm" onClick={bulkApprove} disabled={bulkApproving || approvable === 0} className="bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50" data-testid="bulk-approve-btn">
                  {bulkApproving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                  Aprobar {approvable} apto{approvable === 1 ? '' : 's'} de {visibleItems.length}
                </Button>
              </>
            );
          })()}
        </div>
      </div>

      {/* List
          IMPORTANT: this JSX is memoized on `items` + a few other props that affect
          rendering. It intentionally does NOT depend on `editing` / `previewing` /
          `pendingRegionId`, so opening a modal or typing in it never re-renders the
          244 event cards. Click handlers use stable state setters, so the memo'd
          JSX still fires the latest state updates. */}
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : visibleItems.length === 0 ? (
        <div className="py-12 text-center text-gray-400 border border-dashed rounded-2xl">
          {items.length === 0
            ? `Sin eventos ${STATUS_LABEL[statusFilter].toLowerCase()}. Ejecuta el scraper para poblar la lista.`
            : `No hay eventos ${STATUS_LABEL[statusFilter].toLowerCase()} desde la fecha seleccionada (${items.length} ocultos por el filtro de fecha de corte).`}
        </div>
      ) : (
        <ScrapedItemsGrid
          items={visibleItems}
          enrichingId={enrichingId}
          onPreview={setPreviewing}
          onEnrich={enrichOne}
          onEdit={openEditModal}
          onApprove={approve}
          onReject={reject}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          showSelection={statusFilter === 'pending'}
        />
      )}

      {/* Edit modal — mirrors the fields available when creating a PlanCity event,
          in the same order as the provider Dashboard modal. */}
      {/* Preview modal — how the event will look on PlanCity once approved */}
      <Dialog open={!!previewing} onOpenChange={(v) => !v && setPreviewing(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0">
          {previewing && (
            <>
              <div className="relative aspect-[16/9] bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center">
                {previewing.main_image ? (
                  <img
                    src={previewing.main_image}
                    alt={previewing.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const parent = e.target.parentNode;
                      if (!parent) return;
                      const letter = (previewing.source || '?').charAt(0).toUpperCase();
                      e.target.outerHTML = `<div class="flex flex-col items-center justify-center text-emerald-700"><span class="text-6xl font-black">${letter}</span><span class="text-xs uppercase mt-1 opacity-70">sin imagen</span></div>`;
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-emerald-700">
                    <span className="text-6xl font-black">{(previewing.source || '?').charAt(0).toUpperCase()}</span>
                    <span className="text-xs uppercase mt-1 opacity-70">sin imagen</span>
                  </div>
                )}
                {/* Source badge */}
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-emerald-800 shadow-sm" data-testid="preview-source-badge">
                  {previewing.source}
                </div>
                {previewing.ai_enriched && (
                  <div className="absolute top-3 right-3 bg-purple-500/90 text-white px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider shadow-sm">
                    ✨ IA
                  </div>
                )}
              </div>
              <div className="p-6 space-y-3 max-h-[50vh] overflow-y-auto">
                <div>
                  <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Vista previa PlanCity</p>
                  <h2 className="text-xl font-bold text-[#064E3B] mt-0.5 leading-snug" data-testid="preview-title">{previewing.name}</h2>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {previewing.start_date && (
                    <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 px-2.5 py-1 rounded-md text-xs font-semibold" data-testid="preview-date">
                      📅 {new Date(previewing.start_date).toLocaleDateString('es-CL', { weekday: 'short', day: '2-digit', month: 'long', timeZone: 'America/Santiago' })}
                      {' · '}
                      {new Date(previewing.start_date).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Santiago' })} hrs
                    </span>
                  )}
                  {previewing.commune_name && (
                    <span className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-700 px-2.5 py-1 rounded-md text-xs font-semibold">
                      📍 {previewing.commune_name}
                    </span>
                  )}
                  {previewing.venue_name && (
                    <span className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-700 px-2.5 py-1 rounded-md text-xs font-semibold">
                      🏛️ {previewing.venue_name}
                    </span>
                  )}
                  {(previewing.is_free || (previewing.price !== null && previewing.price !== undefined)) && (
                    <span className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-2.5 py-1 rounded-md text-xs font-bold">
                      {previewing.is_free ? 'GRATIS' : `$${Math.round(previewing.price).toLocaleString('es-CL')}`}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {previewing.category_name && <Badge className="bg-emerald-50 text-emerald-700 border-0">{previewing.category_name}</Badge>}
                  {previewing.category_name_2 && <Badge className="bg-emerald-50 text-emerald-700 border-0">{previewing.category_name_2}</Badge>}
                  {previewing.target_audience && <Badge className="bg-gray-100 text-gray-700 border-0">👥 {previewing.target_audience}</Badge>}
                </div>
                {previewing.short_description && (
                  <p className="text-sm text-gray-600 italic border-l-4 border-emerald-200 pl-3">&ldquo;{previewing.short_description}&rdquo;</p>
                )}
                {previewing.description && (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" data-testid="preview-description">
                    {previewing.description}
                  </div>
                )}
              </div>
              <div className="border-t bg-gray-50 px-6 py-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {previewing.source_url && (
                    <a href={previewing.source_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 inline-flex items-center gap-1 hover:underline">
                      <ExternalLink className="w-3.5 h-3.5" /> Fuente original
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {previewing.status === 'pending' && (
                    <>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                        onClick={() => { reject(previewing.id); setPreviewing(null); }}
                        data-testid="preview-reject">
                        Rechazar
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => { const it = previewing; setPreviewing(null); openEditModal(it); }}
                        data-testid="preview-edit">
                        Editar
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => { approve(previewing.id); setPreviewing(null); }}
                        data-testid="preview-approve">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Aprobar
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setPendingRegionId(''); } }}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] p-0 flex flex-col gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle>Editar evento scrapeado</DialogTitle>
            <DialogDescription>Ajusta cada campo como si crearas el evento en PlanCity. Los cambios NO se envían a la fuente.</DialogDescription>
            {(editing?.source_url || editing?.raw_data?.vamopo_url) && (
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                {editing?.source_url && (
                  <a
                    href={editing.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-900 underline decoration-dotted underline-offset-2"
                    data-testid="edit-source-url-link"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Fuente original ({(() => {
                      try { return new URL(editing.source_url).hostname.replace(/^www\./, '').split('.')[0]; }
                      catch { return editing.source || 'fuente'; }
                    })()})
                  </a>
                )}
                {editing?.raw_data?.vamopo_url && editing.raw_data.vamopo_url !== editing.source_url && (
                  <a
                    href={editing.raw_data.vamopo_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 underline decoration-dotted underline-offset-2"
                    data-testid="edit-vamopo-url-link"
                  >
                    <Globe className="w-3.5 h-3.5" />
                    Ver en Vamopo (agregador)
                  </a>
                )}
              </div>
            )}
          </DialogHeader>
          {editing && (
            <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
              {/* 1. Nombre */}
              <div>
                <Label>Nombre del evento *</Label>
                <Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-name" />
              </div>

              {/* 2. Categoría + Categoría adicional */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Categoría *</Label>
                  <Select value={editing.category_id || undefined} onValueChange={(v) => setEditing({ ...editing, category_id: v })}>
                    <SelectTrigger data-testid="edit-category"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Categoría adicional (opcional)</Label>
                  <Select value={editing.category_id_2 || undefined} onValueChange={(v) => setEditing({ ...editing, category_id_2: v })}>
                    <SelectTrigger data-testid="edit-category-2"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{categories.filter((c) => c.id !== editing.category_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* 3. Descripción corta + Descripción */}
              <div>
                <Label>Descripción corta</Label>
                <Input value={editing.short_description || ''} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} maxLength={280} data-testid="edit-short-desc" />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea rows={5} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="edit-desc" />
              </div>

              {/* 4. Toggles */}
              <div className="rounded-xl border border-gray-200 p-3">
                <div className="flex items-center gap-4 flex-wrap text-sm">
                  <label className="flex items-center gap-2">
                    <Switch checked={!!editing.is_permanent} onCheckedChange={(v) => setEditing({ ...editing, is_permanent: v, is_recurring: v ? false : editing.is_recurring })} data-testid="edit-is-permanent" />
                    Evento permanente
                  </label>
                  <label className="flex items-center gap-2">
                    <Switch checked={!!editing.is_recurring} onCheckedChange={(v) => setEditing({ ...editing, is_recurring: v, is_permanent: v ? false : editing.is_permanent })} data-testid="edit-is-recurring" />
                    Evento recurrente
                  </label>
                  <label className="flex items-center gap-2">
                    <Switch checked={!!editing.is_online} onCheckedChange={(v) => setEditing({ ...editing, is_online: v })} data-testid="edit-is-online" />
                    Evento online
                  </label>
                </div>
              </div>

              {/* 5. Fechas / horario */}
              {!editing.is_permanent && !editing.is_recurring && (
                <div className="rounded-xl border border-gray-200 p-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1 mb-2"><Clock className="w-3 h-3" /> Fechas</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Fecha y hora inicio *</Label>
                      <Input
                        type="datetime-local"
                        value={editing.start_date ? editing.start_date.slice(0, 16) : ''}
                        onChange={(e) => {
                          const newStart = e.target.value;
                          setEditing((prev) => {
                            const next = { ...prev, start_date: newStart };
                            // Default end_date to start + 3h whenever start changes and
                            // the current end is empty or was itself an auto-generated default
                            // (either 23:59 same-day or start+3h from a previous edit).
                            if (newStart) {
                              const [dPart, tPart] = newStart.split('T');
                              const [y, m, d] = dPart.split('-').map(Number);
                              const [hh, mm] = (tPart || '00:00').split(':').map(Number);
                              const startDt = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
                              const endDt = new Date(startDt.getTime() + 3 * 60 * 60 * 1000);
                              const pad = (n) => String(n).padStart(2, '0');
                              const autoEnd = `${endDt.getFullYear()}-${pad(endDt.getMonth() + 1)}-${pad(endDt.getDate())}T${pad(endDt.getHours())}:${pad(endDt.getMinutes())}`;
                              const currentEndDay = prev.end_date ? prev.end_date.slice(0, 10) : '';
                              const currentEndTime = prev.end_date ? prev.end_date.slice(11, 16) : '';
                              const endIsBlankOrAutoDefault = !prev.end_date || currentEndDay !== dPart || currentEndTime === '23:59';
                              if (endIsBlankOrAutoDefault) {
                                next.end_date = autoEnd;
                              }
                            }
                            return next;
                          });
                        }}
                        data-testid="edit-start"
                      />
                    </div>
                    <div>
                      <Label>Fecha y hora término *</Label>
                      <Input type="datetime-local" value={editing.end_date ? editing.end_date.slice(0, 16) : ''} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} data-testid="edit-end" />
                    </div>
                  </div>
                </div>
              )}
              {editing.is_recurring && (
                <div>
                  <Label>Fecha límite del recurrente</Label>
                  <Input type="datetime-local" value={editing.recurring_end_date ? editing.recurring_end_date.slice(0, 16) : ''} onChange={(e) => setEditing({ ...editing, recurring_end_date: e.target.value })} data-testid="edit-recurring-end" />
                </div>
              )}
              {(editing.is_recurring || editing.is_permanent) && (
                <div>
                  <Label>Horario (texto libre)</Label>
                  <Input value={editing.schedule || ''} onChange={(e) => setEditing({ ...editing, schedule: e.target.value })} placeholder="Ej: Lun a Vie 10:00-18:00" data-testid="edit-schedule" />
                </div>
              )}

              {/* 6. Detalles online */}
              {editing.is_online && (
                <div className="rounded-xl border border-violet-200 p-3 bg-violet-50/50 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="flex items-center gap-1 text-violet-700"><Link2 className="w-3 h-3" /> Plataforma</Label>
                    <Input value={editing.online_platform || ''} onChange={(e) => setEditing({ ...editing, online_platform: e.target.value })} placeholder="Zoom, YouTube, Twitch…" data-testid="edit-online-platform" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1 text-violet-700"><Link2 className="w-3 h-3" /> URL del evento</Label>
                    <Input value={editing.online_url || ''} onChange={(e) => setEditing({ ...editing, online_url: e.target.value })} placeholder="https://…" data-testid="edit-online-url" />
                  </div>
                </div>
              )}

              {/* 6b. Venta de entradas (opcional) — link externo para comprar */}
              {!editing.is_online && (
                <div className="rounded-xl border border-amber-200 p-3 bg-amber-50/40">
                  <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 uppercase tracking-wider mb-2">
                    <span>🎫 Venta de entradas</span>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!editing._use_source_as_ticket || (editing.online_url && editing.source_url && editing.online_url === editing.source_url)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setEditing((prev) => ({
                          ...prev,
                          _use_source_as_ticket: checked,
                          online_url: checked ? (prev.source_url || '') : (prev._use_source_as_ticket ? '' : prev.online_url || ''),
                        }));
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      data-testid="edit-use-source-ticket"
                    />
                    Usar la URL de la fuente original como link de venta
                    {editing.source_url && (
                      <span className="text-[11px] text-gray-500 truncate ml-1">({(() => { try { return new URL(editing.source_url).hostname.replace(/^www\./,''); } catch { return 'fuente'; } })()})</span>
                    )}
                  </label>
                  <div className="mt-2">
                    <Label className="flex items-center gap-1 text-amber-800"><Link2 className="w-3 h-3" /> URL para comprar entradas (opcional)</Label>
                    <Input
                      value={editing.online_url || ''}
                      onChange={(e) => setEditing((prev) => ({ ...prev, online_url: e.target.value, _use_source_as_ticket: false }))}
                      placeholder="https://… (deja vacío si es venta en puerta / no tiene link)"
                      disabled={!!editing._use_source_as_ticket || (!!editing.online_url && editing.online_url === editing.source_url)}
                      data-testid="edit-ticket-url"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Este link aparece en el evento público como botón &quot;Comprar entradas&quot;.</p>
                  </div>
                </div>
              )}

              {/* 7. Ubicación — Region → Comuna → Dirección con Map picker */}
              {!editing.is_online && (
                <div className="rounded-xl border border-gray-200 p-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1 mb-2"><MapPin className="w-3 h-3" /> Ubicación</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Región *</Label>
                      <Select
                        value={selectedRegionId || undefined}
                        onValueChange={(regionId) => {
                          setPendingRegionId(regionId);
                          // Reset commune if it doesn't belong to the new region.
                          const currentCom = communes.find((c) => c.id === editing.commune_id);
                          if (currentCom && currentCom.region_id !== regionId) {
                            setEditing({ ...editing, commune_id: null });
                          }
                        }}
                      >
                        <SelectTrigger data-testid="edit-region"><SelectValue placeholder="Elige región" /></SelectTrigger>
                        <SelectContent className="max-h-72">{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Comuna *</Label>
                      <Select value={editing.commune_id || undefined} onValueChange={(v) => setEditing({ ...editing, commune_id: v })}>
                        <SelectTrigger data-testid="edit-commune"><SelectValue placeholder="Elige comuna" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {(selectedRegionId ? communes.filter((c) => c.region_id === selectedRegionId) : communes).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Dirección</Label>
                    <div className="flex gap-2">
                      <Input value={editing.address || ''} onChange={(e) => setEditing({ ...editing, address: e.target.value })} placeholder="Providencia 1234" data-testid="edit-address" className="flex-1" />
                      <Button type="button" variant="outline" onClick={() => setLocationPickerOpen(true)} data-testid="edit-open-map" className="whitespace-nowrap">
                        <MapPin className="w-4 h-4 mr-1" /> Buscar en mapa
                      </Button>
                    </div>
                    {(editing.latitude && editing.longitude) ? (
                      <div className="mt-1.5 flex items-center gap-2" data-testid="edit-geo-status">
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 border border-green-200 px-1.5 py-0.5 rounded-full text-[11px] font-semibold">
                          <MapPin className="w-3 h-3" /> En mapa
                        </span>
                        <span className="text-[11px] text-gray-500">{editing.latitude.toFixed(5)}, {editing.longitude.toFixed(5)}</span>
                      </div>
                    ) : (
                      <div className="mt-1.5 flex items-center gap-2" data-testid="edit-geo-status">
                        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded-full text-[11px] font-semibold">
                          <MapPin className="w-3 h-3 opacity-60" /> Sin coordenadas
                        </span>
                        <span className="text-[11px] text-gray-500">No aparecerá en el mapa. Usa <b>Buscar en mapa</b> para geolocalizar.</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-2">
                    <Label>Recinto / Lugar (opcional)</Label>
                    <VenueAutocomplete
                      label={null}
                      placeholder="Escribe el nombre del lugar (ej: Teatro Sidarte)…"
                      initialText={editing.venue_name || ''}
                      communeId={editing.commune_id}
                      regionId={selectedRegionId}
                      onTextChange={(v) => setEditing((prev) => ({ ...prev, venue_name: v }))}
                      onSelect={(v) => {
                        setPendingRegionId(v.region_id || '');
                        setEditing((prev) => ({
                          ...prev,
                          venue_name: v.name,
                          address: v.address || prev.address,
                          latitude: v.latitude ?? prev.latitude,
                          longitude: v.longitude ?? prev.longitude,
                          commune_id: v.commune_id || prev.commune_id,
                        }));
                        toast.success(`Autocompletado desde: ${v.name}`);
                      }}
                      testId="edit-venue-autocomplete"
                    />
                  </div>
                </div>
              )}

              {/* 8. Precio — same UX as PlanCity provider modal */}
              <div>
                <Label className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> Precio (CLP)</Label>
                <Input
                  type="number" min="0"
                  value={editing.price ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') return setEditing({ ...editing, price: null, is_free: false });
                    const n = parseFloat(v);
                    setEditing({ ...editing, price: n, is_free: n === 0 });
                  }}
                  placeholder="0 = gratuito · dejar vacío = precio no informado"
                  data-testid="edit-price"
                />
                <label className="flex items-center gap-2 text-sm mt-2">
                  <Switch checked={!!editing.price_is_from} onCheckedChange={(v) => setEditing({ ...editing, price_is_from: v })} data-testid="edit-price-is-from" />
                  Precio desde (indicativo)
                </label>
              </div>

              {/* 9. Foto principal */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Foto principal *</Label>
                  {editing.main_image && (
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, main_image: '' })}
                      className="text-xs text-red-600 hover:text-red-800 underline decoration-dotted"
                      data-testid="edit-clear-image"
                    >
                      Borrar imagen
                    </button>
                  )}
                </div>
                <Input value={editing.main_image || ''} onChange={(e) => setEditing({ ...editing, main_image: e.target.value })} placeholder="URL de la imagen" data-testid="edit-image" />
                {editing.main_image && <img src={editing.main_image} alt="preview" className="w-full max-h-52 object-cover rounded mt-2" onError={(e) => (e.target.style.opacity = 0.2)} />}
              </div>

              {/* 10. Público objetivo + Instagram */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1"><Users className="w-3 h-3" /> Público objetivo</Label>
                  <Select value={editing.target_audience || undefined} onValueChange={(v) => setEditing({ ...editing, target_audience: v })}>
                    <SelectTrigger data-testid="edit-audience"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {[['all','Todo público'],['family','Familia'],['kids','Niños'],['teen','Adolescentes'],['adult','Adultos']].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <OrganizerAutocomplete
                    label="Instagram del organizador"
                    placeholder="Empieza a escribir el organizador…"
                    initialText={editing.organizer_instagram || ''}
                    onTextChange={(v) => setEditing((prev) => ({ ...prev, organizer_instagram: v }))}
                    onSelect={(org) => {
                      if (org.instagram) {
                        setEditing((prev) => ({ ...prev, organizer_instagram: org.instagram }));
                        toast.success(`IG autocompletado desde ${org.name}`);
                      }
                    }}
                    testId="edit-organizer-autocomplete"
                  />
                </div>
              </div>

              <div className="h-2" />
            </div>
          )}
          {editing && (
            <div className="border-t bg-white px-6 py-3 flex flex-wrap justify-end gap-2 shrink-0">
              <Button variant="outline" onClick={() => { setEditing(null); setPendingRegionId(''); }}>Cancelar</Button>
              <Button
                variant="outline"
                onClick={() => {
                  const id = editing.id;
                  setEditing(null);
                  setPendingRegionId('');
                  reject(id);
                }}
                disabled={saveAndPublishing}
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid="edit-reject"
                title="Descarta este evento sin publicarlo"
              >
                Rechazar
              </Button>
              <Button
                variant="outline"
                onClick={saveEdit}
                data-testid="edit-save"
                disabled={saveAndPublishing}
              >
                Guardar cambios
              </Button>
              <Button
                onClick={() => saveAndPublish(false)}
                disabled={saveAndPublishing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="edit-save-and-publish"
                title="Guarda los cambios y publica el evento en un solo paso"
              >
                {saveAndPublishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Guardar y publicar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Address → coordinates picker (nominatim + Leaflet). Reused from PlanCity provider modal. */}
      {editing && (
        <LocationPickerModal
          open={locationPickerOpen}
          onClose={() => setLocationPickerOpen(false)}
          initialAddress={editing.address || ''}
          initialLat={editing.latitude ? parseFloat(editing.latitude) : null}
          initialLng={editing.longitude ? parseFloat(editing.longitude) : null}
          initialCommuneId={editing.commune_id || ''}
          communes={communes}
          onConfirm={(lat, lng, address) => {
            setEditing((prev) => ({
              ...prev,
              latitude: lat,
              longitude: lng,
              // Only overwrite address if we resolved one AND the field was
              // empty OR still contained the previous auto-filled value.
              // The admin's manual edits take precedence over placeholders.
              address: address || prev?.address || '',
            }));
          }}
        />
      )}

      {/* Duplicate detected — confirm bypass */}
      <Dialog open={!!duplicateModal} onOpenChange={(o) => !o && setDuplicateModal(null)}>
        <DialogContent className="max-w-lg" data-testid="duplicate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <span>⚠️</span> Ya existe un evento muy similar
            </DialogTitle>
          </DialogHeader>
          {duplicateModal?.existing && (
            <div className="space-y-3 text-sm">
              <p className="text-gray-600">
                Coincide por <b>nombre casi igual</b> + <b>fecha ±6h</b> + <b>mismo recinto o comuna</b>.
                Si publicas igual, se registrará como duplicado en el reporte de admin y se notificará por Telegram.
              </p>
              <div className="rounded border border-amber-200 bg-amber-50 p-3 space-y-1">
                <div className="text-xs font-semibold text-amber-800 uppercase">Evento existente</div>
                <div className="font-semibold">{duplicateModal.existing.name}</div>
                <div className="text-xs text-gray-600">
                  📅 {duplicateModal.existing.start_date ? new Date(duplicateModal.existing.start_date).toLocaleString('es-CL', { timeZone: 'America/Santiago' }) : '—'}
                </div>
                {duplicateModal.existing.venue_name && (
                  <div className="text-xs text-gray-600">📍 {duplicateModal.existing.venue_name}</div>
                )}
                {duplicateModal.existing.commune_name && (
                  <div className="text-xs text-gray-600">Comuna: {duplicateModal.existing.commune_name}</div>
                )}
                <div className="text-xs text-gray-500">Estado: {duplicateModal.existing.status}</div>
                <a
                  href={`/event/${duplicateModal.existing.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline mt-1"
                >
                  Ver evento existente →
                </a>
              </div>
              {duplicateModal.scrapedName && (
                <div className="rounded border border-blue-200 bg-blue-50 p-3">
                  <div className="text-xs font-semibold text-blue-800 uppercase">Nuevo scraped</div>
                  <div className="font-semibold">{duplicateModal.scrapedName}</div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDuplicateModal(null)} data-testid="duplicate-cancel">
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={async () => {
                const src = duplicateModal.source;
                const id = duplicateModal.scrapedId;
                setDuplicateModal(null);
                if (src === 'edit-save-publish') {
                  await saveAndPublish(true);
                } else {
                  await doApprove(id, true);
                }
              }}
              data-testid="duplicate-publish-anyway"
            >
              Publicar igual
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Memoized event card. Wrapped in React.memo so opening/typing in the edit
// modal doesn't re-render 200+ cards on every keystroke.
const ScrapedEventCard = React.memo(function ScrapedEventCard({ it, enrichingId, onPreview, onEnrich, onEdit, onApprove, onReject, selected, onToggleSelect }) {
  return (
    <div
      key={it.id}
      data-testid={`scraped-item-${it.id}`}
      onClick={() => onPreview(it)}
      className={`rounded-2xl bg-white border p-3 hover:shadow-md transition flex gap-3 cursor-pointer ${selected ? 'border-red-400 ring-2 ring-red-100' : 'hover:border-emerald-300'}`}
      role="button"
      tabIndex={0}
    >
      {onToggleSelect && (
        <label
          className="flex items-start pt-1 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(it.id)}
            className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
            data-testid={`select-${it.id}`}
            aria-label="Seleccionar para rechazar"
          />
        </label>
      )}
      {it.main_image ? (
        <img
          src={it.main_image}
          alt={it.name}
          className="w-24 h-24 rounded-lg object-cover shrink-0 bg-gray-100"
          onError={(e) => {
            const parent = e.target.parentNode;
            if (!parent) return;
            const src = (it.source || '?').charAt(0).toUpperCase();
            e.target.outerHTML = `<div class="w-24 h-24 rounded-lg shrink-0 flex items-center justify-center bg-gradient-to-br from-emerald-100 to-emerald-50 text-emerald-700 text-2xl font-black" title="Imagen no disponible en la fuente">${src}</div>`;
          }}
        />
      ) : (
        <div className="w-24 h-24 rounded-lg shrink-0 flex flex-col items-center justify-center bg-gradient-to-br from-emerald-100 to-emerald-50 text-emerald-700" title="Sin imagen desde la fuente">
          <span className="text-2xl font-black">{(it.source || '?').charAt(0).toUpperCase()}</span>
          <span className="text-[9px] uppercase mt-0.5 opacity-70">sin img</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-semibold text-sm text-[#064E3B] line-clamp-2">{it.name}</h4>
          <Badge className={`${STATUS_COLOR[it.status]} border-0 text-[10px]`}>{STATUS_LABEL[it.status]}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500 mt-1">
          {(() => {
            if (!it.start_date) {
              return (
                <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-semibold border border-amber-200" title="La fuente no entregó fecha — edita el evento para agregarla">
                  ⚠️ Sin fecha
                </span>
              );
            }
            const start = new Date(it.start_date);
            const now = new Date();
            // A recurring / permanent event never "expires"; for scraping
            // moderation, an event whose START is already in the past is
            // considered expired even if end_date is still in the future
            // (multi-day events lose value once they've kicked off).
            const isPast = !it.is_permanent && !it.is_recurring && start < now;
            return (
              <>
                <span
                  className="bg-emerald-600/10 text-emerald-800 px-1.5 py-0.5 rounded font-semibold"
                  title={start.toLocaleString('es-CL', { timeZone: 'America/Santiago' })}
                >
                  📅 {start.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', timeZone: 'America/Santiago' })}
                  {' · '}
                  {start.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Santiago' })}
                  {' hrs'}
                </span>
                {isPast ? (
                  <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold border border-red-200">🔴 Vencido</span>
                ) : (
                  <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded font-semibold border border-green-200">🟢 Vigente</span>
                )}
              </>
            );
          })()}
          {it.category_name && <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{it.category_name}</span>}
          {it.category_name_2 && <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{it.category_name_2}</span>}
          {it.commune_name ? <span>· {it.commune_name}</span> : it.status === 'pending' && <span className="bg-red-50 text-red-700 px-1.5 py-0.5 rounded font-semibold">⚠️ Falta comuna</span>}
          {(it.latitude != null && it.longitude != null) ? (
            <span
              className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded font-semibold border border-green-200"
              title={`En el mapa: ${it.latitude.toFixed(5)}, ${it.longitude.toFixed(5)}`}
              data-testid={`scraped-badge-geo-${it.id}`}
            >📍 En mapa</span>
          ) : (
            <span
              className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-semibold border border-gray-200"
              title="Sin coordenadas — no aparecerá en el mapa al aprobar"
              data-testid={`scraped-badge-nogeo-${it.id}`}
            >📍 Sin coord.</span>
          )}
          {it.target_audience && <span>· 👥 {it.target_audience}</span>}
          {it.ai_enriched && <span className="text-purple-600 font-medium">✨ IA</span>}
        </div>
        {it.short_description && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{it.short_description}</p>}
        <div className="flex items-center gap-1.5 mt-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
          {it.source_url && (
            <a href={it.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-gray-500 hover:text-emerald-700 inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <ExternalLink className="w-3 h-3" /> Fuente
            </a>
          )}
          {it.status === 'pending' && (
            <>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); onEnrich(it.id); }} disabled={enrichingId === it.id} data-testid={`enrich-${it.id}`}>
                {enrichingId === it.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                {it.ai_enriched ? 'Re-enriquecer' : 'Enriquecer IA'}
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); onEdit(it); }} data-testid={`edit-${it.id}`}>Editar</Button>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(e) => { e.stopPropagation(); onApprove(it.id); }} data-testid={`approve-${it.id}`}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Aprobar
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); onReject(it.id); }} data-testid={`reject-${it.id}`}>
                <XCircle className="w-3 h-3 mr-1" /> Rechazar
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

// Grid wrapper — memoized. Only re-renders when the items array or enrichingId
// change, NOT on every parent state update (opening the edit modal, typing, etc.).
const ScrapedItemsGrid = React.memo(function ScrapedItemsGrid({ items, enrichingId, onPreview, onEnrich, onEdit, onApprove, onReject, selectedIds, onToggleSelect, showSelection }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {items.map((it) => (
        <ScrapedEventCard
          key={it.id}
          it={it}
          enrichingId={enrichingId}
          onPreview={onPreview}
          onEnrich={onEnrich}
          onEdit={onEdit}
          onApprove={onApprove}
          onReject={onReject}
          selected={showSelection && selectedIds ? selectedIds.has(it.id) : false}
          onToggleSelect={showSelection ? onToggleSelect : null}
        />
      ))}
    </div>
  );
});
